<script setup>
// Tela de conta suspensa. Fica fora do shell (sem Hub, sem dock), então
// precisa oferecer a saída ela mesma: trocar para outra empresa ativa, quando
// houver, e sair. Antes a pessoa que opera várias contas e caía numa suspensa
// ficava presa aqui.
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Auth from 'dashboard/api/auth';

const { t } = useI18n();
const route = useRoute();
const currentUser = useMapGetter('getCurrentUser');

const otherAccounts = computed(() =>
  [...(currentUser.value?.accounts || [])]
    .filter(
      account =>
        String(account.id) !== String(route.params.accountId) &&
        account.status !== 'suspended'
    )
    .sort((a, b) => a.name.localeCompare(b.name))
);

// Recarrega a página inteira, como o seletor de empresa do Hub: o estado da
// conta suspensa não pode vazar para a outra.
const openAccount = id => {
  window.location.href = `/app/accounts/${id}/dashboard`;
};

const logout = () => Auth.logout();

// O widget de suporte só existe quando está carregado na página.
const showSupportWidget = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggleBubbleVisibility('show');
  }
};

onMounted(() => {
  showSupportWidget();
  window.addEventListener('chatwoot:on-message', showSupportWidget);
});

onBeforeUnmount(() => {
  window.removeEventListener('chatwoot:on-message', showSupportWidget);
});
</script>

<template>
  <div
    class="flex h-full w-full flex-col items-center justify-center gap-6 overflow-y-auto bg-n-slate-2 px-4 py-8"
  >
    <EmptyState
      :title="t('APP_GLOBAL.ACCOUNT_SUSPENDED.TITLE')"
      :message="t('APP_GLOBAL.ACCOUNT_SUSPENDED.MESSAGE')"
    />
    <section
      v-if="otherAccounts.length"
      class="flex w-full max-w-sm flex-col gap-2"
      data-testid="suspended-other-accounts"
    >
      <h2 class="m-0 text-sm font-semibold text-n-slate-11">
        {{ t('APP_GLOBAL.ACCOUNT_SUSPENDED.SWITCH_ACCOUNT') }}
      </h2>
      <button
        v-for="account in otherAccounts"
        :key="account.id"
        type="button"
        class="flex min-h-11 w-full items-center gap-3 rounded-xl border border-n-weak bg-n-solid-1 px-4 text-left text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
        :data-testid="`suspended-account-${account.id}`"
        @click="openAccount(account.id)"
      >
        <span
          class="i-ri-building-2-line size-4 shrink-0 text-n-slate-11"
          aria-hidden="true"
        />
        <span class="min-w-0 flex-1 truncate">{{ account.name }}</span>
        <span
          class="i-ri-arrow-right-line size-4 shrink-0 text-n-slate-11"
          aria-hidden="true"
        />
      </button>
    </section>
    <NextButton
      variant="outline"
      color="slate"
      class="min-h-11"
      icon="i-ri-logout-box-r-line"
      :label="t('APP_GLOBAL.ACCOUNT_SUSPENDED.LOGOUT')"
      data-testid="suspended-logout"
      @click="logout"
    />
  </div>
</template>
